import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {createTable} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {UpdateListType} from '../../../component/type'
import {Button, List as ListView, Popup} from '../../../component'
import ListRight from '../../../component/listRight'
import {
  getBenefitList,
  getBenefitDetail,
  clearBenefitDetail,
  deleteBenefit,
} from '../../../store/actions'
import {useSelector, useAppDispatch} from '../../../store'
import {BenefitType} from '../../../store/actions/types'
import {UpdateButtonList, RemoveButtonList} from '../../../component/List/Commands'
import {defaultPageSize} from '../../../configs/paginationSetting'

const table = createTable().setRowType<BenefitType>()

const BenefitsList = memo(() => {
  const {t} = useTranslation()
  const navigation = useNavigate()
  const {list, total, from} = useSelector(({benefit}) => ({
    list: benefit.benefitList,
    total: benefit.total,
    from: benefit.from,
  }))
  const [selectedRow, setSelectedRow] = useState<BenefitType>()
  const [isOpenPopup, setIsOpenPopup] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(0)
  const [loading, setLoading] = useState(false)
  const defaultOptions = useMemo(() => ({page: 1, per_page: defaultPageSize}), [])
  const [options, setOptions] = useState(defaultOptions)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getBenefitList(options))
  }, [options])

  const columns = useMemo(
    () => [
      table.createDataColumn('id', {
        header: 'NUMBER',
        cell: ({row}) => <span>{String(row.index + from).padStart(2, '0')}</span>,
      }),
      table.createDataColumn('medias_model', {
        header: 'ICON',
        cell: ({row}) => (
          <span>
            {row.original?.medias_model &&
              row.original.medias_model?.length > 0 &&
              row.original.medias_model[0] && (
                <img
                  src={row.original.medias_model[0].original_url}
                  alt=""
                  className="img_preview-benefits"
                />
              )}
          </span>
        ),
      }),
      table.createDataColumn('title', {
        size: 250,
        header: 'HEADING',
        cell: ({row}) => <span>{row.original?.title}</span>,
      }),
      table.createDataColumn('description', {
        header: 'BODY TEXT',
        size: 800,
        cell: ({row}) => <span>{row.original?.description}</span>,
      }),
      table.createDataColumn('id', {
        header: '',
        cell: ({row}) =>
          row.original && (
            <ListRight
              row={row.original}
              onClick={onUpdatePress}
              buttonList={[UpdateButtonList, RemoveButtonList]}
            />
          ),
      }),
    ],
    [],
  )

  const onUpdatePress = useCallback(
    async ({id, command}: UpdateListType) => {
      if (command === 'update') {
        const response = await dispatch(getBenefitDetail(id))
        if (response) navigation(`/content-management/benefit/${id}`)
      } else if (command === 'remove') {
        setCurrentItemId(id)
        setIsOpenPopup(true)
      } else if (command === 'deleteConfirmed') {
        setLoading(true)
        await dispatch(deleteBenefit(id, options))
        setIsOpenPopup(false)
        setLoading(false)
      }
      setSelectedRow(undefined)
    },
    [selectedRow, list],
  )

  const onPaginate = useCallback(
    (page: number) => {
      setOptions({...options, page})
    },
    [options],
  )

  const onLimit = useCallback(
    (per_page: number) => {
      setOptions({...options, per_page, page: 1})
    },
    [options],
  )

  const onAddNewPress = useCallback(() => {
    dispatch(clearBenefitDetail())
    navigation(`/content-management/benefit/add`)
  }, [])

  return (
    <div className="table-outer">
      <div className="table-header">
        <div className="c-carousal__header">
          <div className="c-carousal__header__lpart v-center">
            <span className="f-Exo-Bold title-color">{t('benefitsOfGDX').toUpperCase()}</span>
          </div>
          <div className="c-carousal__header__rpart">
            <Button
              icon="c-icon--plus"
              title={t('addNew')}
              classAddon="c-button--icons f-Exo-Medium"
              onClick={onAddNewPress}
            />
            <Popup
              isOpen={isOpenPopup}
              setIsOpen={setIsOpenPopup}
              onPressDelete={() => onUpdatePress({id: currentItemId, command: 'deleteConfirmed'})}
              title={t('delete')}
              description={t('deleteText')}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <ListView
        columns={columns}
        data={list}
        total={total}
        onPaginate={onPaginate}
        forcePageIndex={options.page - 1}
        options={options}
        isPageSize
        onLimit={onLimit}
        pageSize={options?.per_page}
      />
    </div>
  )
})

export default BenefitsList
